<template>
    <div class="demand-prediction-page">
        <!-- INFO CARDS SECTION -->
        <div class="dashboard-grid">
            <PredictionInfoCard
                icon="fas fa-shopping-bag"
                iconBg="#e8f3ff"
                :value="dashboard.nextSevenDaysPredictedOrder"
                title="Next 7 days"
                subText="Predicted Order Volume"
                :trendValue="12.5"
                bottomColor="#3a7afe"
            />

            <PredictionInfoCard
                icon="fas fa-truck"
                iconBg="#e6fcee"
                :value="dashboard.ridersVansRequired"
                title="Riders/Vans required"
                subText="Logistics Capacity Needed"
                :trendValue="8.3"
                bottomColor="#27ae60"
            />

            <PredictionInfoCard
                icon="fas fa-server"
                iconBg="#f4eaff"
                :value="dashboard.peakPredictedUsage"
                title="Peak predicted usage"
                subText="IT Infrastructure Load"
                :trendValue="5.2"
                bottomColor="#9b59b6"
            />

            <PredictionInfoCard
                icon="fas fa-shield-alt"
                iconBg="#fff3e5"
                :value="dashboard.fraudRiskPrediction"
                title="Risk score (Low)"
                subText="Fraud Risk Prediction"
                :trendValue="-1.1"
                bottomColor="#f39c12"
            />
        </div>
        <div class="charts-grid">
            <div class="chart-card">
                <div class="chart-header">
                    <h3>Order Volume Forecast</h3>
                    <div class="chart-toggles">
                        <label
                            ><input type="checkbox" v-model="showActual" />
                            Actual</label
                        >
                        <label
                            ><input type="checkbox" v-model="showPredicted" />
                            Predicted</label
                        >
                    </div>
                </div>

                <LineChart
                    :labelsData="orderLabels"
                    :chartData="orderDatasets"
                />
            </div>

            <div class="chart-card">
                <div class="chart-header">
                    <h3>Fraud Risk Forecast</h3>

                    <div class="risk-legend">
                        <span class="legend-dot low"></span> Low (0–4%)
                        <span class="legend-dot medium"></span> Medium (4–7%)
                        <span class="legend-dot high"></span> High (7%+)
                    </div>
                </div>

                <AreaChart :labelsData="riskLabels" :valuesData="riskValues" />

                <div class="risk-details">
                    <p><strong>Top Fraud Factors:</strong></p>
                    <ul>
                        <li>Multiple failed payment attempts</li>
                        <li>Unusual delivery address patterns</li>
                        <li>High-value orders from new accounts</li>
                    </ul>
                </div>
            </div>
        </div>
        <h2 class="section-title">Operational Capacity Forecast</h2>

        <div class="capacity-grid">
            <div class="capacity-card">
                <h3 class="chart-title">Logistics Capacity</h3>

                <BarChart
                    :labelsData="capacityLabels"
                    :chartData="logisticsDatasets"
                />

                <div class="capacity-info-row">
                    <div class="capacity-info-box info-blue capacity-metric-box">
                        <i class="fas fa-chart-line"></i>
                        <div>
                            <p class="label">Required Increase</p>
                            <p class="value">{{ logisticRequiredIncreaseValue }}</p>
                        </div>
                    </div>

                    <div class="capacity-info-box info-green capacity-metric-box">
                        <i class="fas fa-clock"></i>
                        <div>
                            <p class="label">Peak Load Time</p>
                            <p class="value">{{ logisticPeakLoadTime }}</p>
                        </div>
                    </div>

                    <div class="capacity-info-box info-orange capacity-metric-box">
                        <i class="fas fa-exclamation-triangle"></i>
                        <div>
                            <p class="label">Capacity Shortfall</p>
                            <p class="value">
                                {{ logisticCapacityShortfallValue }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="capacity-card">
                <h3 class="chart-title">IT Infrastructure Load</h3>

                <BarChart
                    :labelsData="capacityLabels"
                    :chartData="infrastructureDatasets"
                />

                <div class="capacity-info-row">
                    <div class="capacity-info-box info-purple capacity-metric-box">
                        <i class="fas fa-chart-line"></i>
                        <div>
                            <p class="label">Required Increase</p>
                            <p class="value">{{ itRequiredIncreaseValue }}</p>
                        </div>
                    </div>

                    <div class="capacity-info-box info-green capacity-metric-box">
                        <i class="fas fa-clock"></i>
                        <div>
                            <p class="label">Peak Load Time</p>
                            <p class="value">{{ itPeakLoadTime }}</p>
                        </div>
                    </div>

                    <div class="capacity-info-box info-green-light capacity-metric-box">
                        <i class="fas fa-exclamation-circle"></i>
                        <div>
                            <p class="label">Capacity Shortfall</p>
                            <p class="value">{{ itCapacityShortfallValue }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- Prediction Insights -->
        <h2 class="section-title">Prediction Insights</h2>

        <div class="insight-grid">
            <div class="insight-card">
                <div class="insight-icon icon-blue">
                    <i class="fas fa-chart-line"></i>
                </div>
                <h4 class="insight-title">Key Drivers of Demand</h4>

                <ul>
                    <li>Weekend shopping surge</li>
                    <li>Electronics category spike</li>
                    <li>Promotional campaigns effect</li>
                </ul>
            </div>

            <div class="insight-card">
                <div class="insight-icon icon-green">
                    <i class="fas fa-map-marker-alt"></i>
                </div>
                <h4 class="insight-title">Markets with Rising Trend</h4>

                <ul>
                    <li
                        v-for="(
                            market, index
                        ) in marketsWithRisingTrendList"
                        :key="index"
                    >
                        {{ market.name }}: {{ market.growth }} growth
                    </li>
                </ul>
            </div>

            <div class="insight-card">
                <div class="insight-icon icon-orange">
                    <i class="fas fa-box"></i>
                </div>
                <h4 class="insight-title">Categories with Declining Demand</h4>

                <ul>
                    <li
                        v-for="(
                            category, index
                        ) in categoriesWithDecliningDemandList"
                        :key="index"
                    >
                        {{ category.name }}: {{ category.decline }} decline
                    </li>
                </ul>
            </div>

            <div class="insight-card">
                <div class="insight-icon icon-purple">
                    <i class="fas fa-lightbulb"></i>
                </div>
                <h4 class="insight-title">Suggested Adjustments</h4>

                <ul>
                    <li>Increase rider shifts on Sat</li>
                    <li>Scale server capacity by 25%</li>
                    <li>Enable fraud alerts on Friday</li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
import PredictionInfoCard from "@/components/Generics/PredictionInfoCard.vue";
import LineChart from "@/components/chart/LineChart.vue";
import AreaChart from "@/components/chart/AreaChart.vue";
import BarChart from "@/components/chart/BarChartv2.vue";

export default {
    components: {
        PredictionInfoCard,
        LineChart,
        AreaChart,
        BarChart,
    },

    data() {
        return {
            dashboard: {
                nextSevenDaysPredictedOrder: 0,
                ridersVansRequired: 0,
                peakPredictedUsage: 0,
                fraudRiskPrediction: 0,
            },
            orderVolumeForecast: [],
            fraudRiskForecast: [],
            logisticsCapacity: [],
            itInfrastructureLoad: [],
            fraudRiskPercent: [],
            logisticRequiredIncrease: [],
            logisticCapacityShortfall: [],
            itRequiredIncrease: [],
            itCapacityShortfall: [],
            marketsWithRisingTrend: [],
            categoriesWithDecliningDemand: [],
            showActual: true,
            showPredicted: true,
        };
    },

    computed: {
        orderLabels() {
            if (
                !this.orderVolumeForecast ||
                !Array.isArray(this.orderVolumeForecast) ||
                this.orderVolumeForecast.length === 0
            ) {
                return ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
            }
            return this.orderVolumeForecast.map((item) => {
                const date = new Date(item.day);
                return date.toLocaleDateString("en-US", { weekday: "short" });
            });
        },
        actualOrders() {
            if (
                !this.orderVolumeForecast ||
                !Array.isArray(this.orderVolumeForecast) ||
                this.orderVolumeForecast.length === 0
            ) {
                return [400, 430, 410, 450, 0, 0, 0];
            }
            return this.orderVolumeForecast.map(
                (item) => parseInt(item.actual_orders) || 0,
            );
        },
        predictedOrders() {
            if (
                !this.orderVolumeForecast ||
                !Array.isArray(this.orderVolumeForecast) ||
                this.orderVolumeForecast.length === 0
            ) {
                return [400, 420, 405, 450, 470, 500, 480];
            }
            return this.orderVolumeForecast.map(
                (item) => parseInt(item.predicted_orders) || 0,
            );
        },
        orderDatasets() {
            let datasets = [];

            if (this.showActual) {
                datasets.push({
                    label: "Actual Orders",
                    data: this.actualOrders,
                    borderColor: "#1D7FF0",
                    backgroundColor: "transparent",
                    borderWidth: 3,
                    tension: 0.4,
                    pointRadius: 4,
                    pointBackgroundColor: "#1D7FF0",
                });
            }

            if (this.showPredicted) {
                datasets.push({
                    label: "Predicted Orders",
                    data: this.predictedOrders,
                    borderColor: "#2ECC71",
                    backgroundColor: "transparent",
                    borderDash: [6, 6],
                    borderWidth: 3,
                    tension: 0.4,
                    pointRadius: 4,
                    pointBackgroundColor: "#2ECC71",
                });
            }

            return datasets;
        },
        riskLabels() {
            if (
                !this.fraudRiskForecast ||
                !Array.isArray(this.fraudRiskForecast) ||
                this.fraudRiskForecast.length === 0
            ) {
                return ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
            }
            return this.fraudRiskForecast.map((item) => {
                const date = new Date(item.day);
                return date.toLocaleDateString("en-US", { weekday: "short" });
            });
        },
        riskValues() {
            if (
                !this.fraudRiskForecast ||
                !Array.isArray(this.fraudRiskForecast) ||
                this.fraudRiskForecast.length === 0
            ) {
                return [2.8, 3.1, 3.5, 3.2, 4.1, 5.0, 3.8];
            }
            return this.fraudRiskForecast.map(
                (item) => parseFloat(item.fraud_risk_percent) || 0,
            );
        },
        capacityLabels() {
            const dataSource =
                this.logisticsCapacity?.length > 0
                    ? this.logisticsCapacity
                    : this.itInfrastructureLoad?.length > 0
                      ? this.itInfrastructureLoad
                      : [];

            if (!dataSource || dataSource.length === 0) {
                return ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
            }

            return dataSource.map((item) => {
                if (item.day) {
                    const date = new Date(item.day);
                    return date.toLocaleDateString("en-US", {
                        weekday: "short",
                    });
                }
                return item.label || item.date || "N/A";
            });
        },
        logisticsDatasets() {
            const deliveriesData = this.logisticsCapacity.map(
                (item) =>
                    parseInt(
                        item.deliveries_per_day ||
                            item.deliveries ||
                            item.value,
                    ) || 0,
            );
            const vehiclesData = this.logisticsCapacity.map(
                (item) =>
                    parseInt(
                        item.vehicles_required || item.vehicles || item.value2,
                    ) || 0,
            );
            return [
                {
                    label: "Deliveries/Day",
                    backgroundColor: "#3498db",
                    data: deliveriesData,
                },
                {
                    label: "Vehicles Required",
                    backgroundColor: "#2ecc71",
                    data: vehiclesData,
                },
            ];
        },
        infrastructureDatasets() {
            const dbLoadData = this.itInfrastructureLoad.map(
                (item) =>
                    parseFloat(
                        item.db_load_percent || item.db_load || item.value,
                    ) || 0,
            );
            const serverUsageData = this.itInfrastructureLoad.map(
                (item) =>
                    parseFloat(
                        item.server_usage_percent ||
                            item.server_usage ||
                            item.value2,
                    ) || 0,
            );

            return [
                {
                    label: "DB Load %",
                    backgroundColor: "#e84393",
                    data: dbLoadData,
                },
                {
                    label: "Server Usage %",
                    backgroundColor: "#8e44ad",
                    data: serverUsageData,
                },
            ];
        },
        logisticRequiredIncreaseValue() {
            const value =
                this.logisticRequiredIncrease?.required_increase_percent
            return this.formatPercentValue(value);
        },
        logisticPeakLoadTime() {
            return "2:00 PM";
        },
        logisticCapacityShortfallValue() {
            const value = this.logisticCapacityShortfall?.capacity_shortfall;
            return typeof value === "number"
                ? `${value} vehicles`
                : value || "0 vehicles";
        },
        itRequiredIncreaseValue() {
            const value = this.itRequiredIncrease?.it_required_increase_percent;
            return this.formatPercentValue(value);
        },
        itPeakLoadTime() {
            return "11:00 AM";
        },
        itCapacityShortfallValue() {
            if (
                !this.itCapacityShortfall ||
                this.itCapacityShortfall.length === 0
            ) {
                return "None";
            }
            const value = this.itCapacityShortfall?.capacity_shortfall;
            if (value === 0 || value === "0" || value === null) {
                return "None";
            }
            return typeof value === "number" ? `${value}` : value || "None";
        },
        marketsWithRisingTrendList() {
            return this.marketsWithRisingTrend.map((item) => ({
                name: item.market || "NA",
                growth: item.last_7 + "%" || "+0%",
            }));
        },
        categoriesWithDecliningDemandList() {
            return this.categoriesWithDecliningDemand.map((item) => ({
                name: item.category || "NA",
                decline: item?.last_7 + "%" || 0 + "%",
            }));
        },
        userId() {
            if (this.$store.getters["auth/getUserID"]) {
                return this.$store.getters["auth/getUserID"];
            } else {
                return JSON.parse(sessionStorage.getItem("userLogin"))["_id"];
            }
        },
    },
    methods: {
        formatPercentValue(value, fallback = "0%") {
            if (value === null || value === undefined || value === "") {
                return fallback;
            }

            const normalized = String(value).trim().replace(/\s*%$/, "");

            return normalized ? `${normalized}%` : fallback;
        },
        getTenant() {
            return (
                this.$currentUserData?.tenant ||
                this.$currentUserData?._id ||
                this.userId
            );
        },
        async fetchDashboardData() {
            const payload = {
                items: [
                    {
                        key: "next-seven-days-predicted-order",
                        variables: {
                            tenant: this.getTenant(),
                        },
                    },
                    {
                        key: "riders-vans-required",
                        variables: {
                            tenant: this.getTenant(),
                        },
                    },
                    {
                        key: "peak-predicted-usage",
                        variables: {
                            tenant: this.getTenant(),
                        },
                    },
                    {
                        key: "fraud-risk-prediction",
                        variables: {
                            tenant: this.getTenant(),
                        },
                    },
                ],
            };
            const response = await this.$store.dispatch(
                "partner/fetchBulkAnalyticReport",
                payload,
            );
            const data = response?.data || response || {};
            this.dashboard.nextSevenDaysPredictedOrder =
                data["next-seven-days-predicted-order"]
                    ?.predicted_orders_next_7_days || 0;
            this.dashboard.ridersVansRequired =
                data["riders-vans-required"]?.peak_predicted_usage_percent || 0;
            this.dashboard.peakPredictedUsage =
                data["peak-predicted-usage"]?.peak_predicted_usage_percent || 0;
            this.dashboard.fraudRiskPrediction =
                data["fraud-risk-prediction"]?.fraud_risk_percent || 0;
        },
        async fetchOrderVolumeForecast() {
            const payload = {
                items: [
                    {
                        key: "order-volume-forecast",
                        variables: {
                            tenant: this.getTenant(),
                        },
                    },
                ],
            };
            const response = await this.$store.dispatch(
                "partner/fetchBulkAnalyticReport",
                payload,
            );
            // Handle both response structures: {status, data} or direct data object
            this.orderVolumeForecast =
                response?.data?.["order-volume-forecast"] ||
                response?.["order-volume-forecast"] ||
                [];
        },
        async fetchFraudRiskForecast() {
            const payload = {
                items: [
                    {
                        key: "fraud-risk-forecast",
                        variables: {
                            tenant: this.getTenant(),
                        },
                    },
                    {
                        key: "fraud_risk_percent",
                        variables: {
                            tenant: this.getTenant(),
                        },
                    },
                ],
            };
            const response = await this.$store.dispatch(
                "partner/fetchBulkAnalyticReport",
                payload,
            );
            this.fraudRiskForecast =
                response?.data?.["fraud-risk-forecast"] ||
                response?.["fraud-risk-forecast"] ||
                [];
            this.fraudRiskPercent =
                response?.data?.["fraud_risk_percent"] ||
                response?.["fraud_risk_percent"] ||
                [];
        },
        async fetchLogisticsCapacity() {
            const payload = {
                items: [
                    {
                        key: "logistics-capacity",
                        variables: {
                            tenant: this.getTenant(),
                        },
                    },
                ],
            };
            const response = await this.$store.dispatch(
                "partner/fetchBulkAnalyticReport",
                payload,
            );
            this.logisticsCapacity =
                response?.data?.["logistics-capacity"] ||
                response?.["logistics-capacity"] ||
                [];
        },
        async fetchItInfrastructureLoad() {
            const payload = {
                items: [
                    {
                        key: "it-infrastructure-load",
                        variables: {
                            tenant: this.getTenant(),
                        },
                    },
                ],
            };
            const response = await this.$store.dispatch(
                "partner/fetchBulkAnalyticReport",
                payload,
            );
            this.itInfrastructureLoad =
                response?.data?.["it-infrastructure-load"] ||
                response?.["it-infrastructure-load"] ||
                [];
        },
        async fetchLogisticData() {
            const payload = {
                items: [
                    {
                        key: "logistic-srequired-increase",
                        variables: {
                            tenant: this.getTenant(),
                        },
                    },
                    {
                        key: "logistic-capacity-shortfall",
                        variables: {
                            tenant: this.getTenant(),
                        },
                    },
                ],
            };
            const response = await this.$store.dispatch(
                "partner/fetchBulkAnalyticReport",
                payload,
            );
            this.logisticRequiredIncrease =
                response?.data?.["logistic-srequired-increase"] ||
                response?.["logistic-srequired-increase"] ||
                response?.data?.["logistic-required-increase"] ||
                response?.["logistic-required-increase"] ||
                [];
            this.logisticCapacityShortfall =
                response?.data?.["logistic-capacity-shortfall"] ||
                response?.["logistic-capacity-shortfall"] ||
                [];
        },
        async fetchItData() {
            const payload = {
                items: [
                    {
                        key: "it-required-increase",
                        variables: {
                            tenant: this.getTenant(),
                        },
                    },
                    {
                        key: "it-capacity-shortfall",
                        variables: {
                            tenant: this.getTenant(),
                        },
                    },
                ],
            };
            const response = await this.$store.dispatch(
                "partner/fetchBulkAnalyticReport",
                payload,
            );
            this.itRequiredIncrease =
                response?.data?.["it-required-increase"] ||
                response?.["it-required-increase"] ||
                [];
            this.itCapacityShortfall =
                response?.data?.["it-capacity-shortfall"] ||
                response?.["it-capacity-shortfall"] ||
                [];
        },
        async fetchPredictionInsights() {
            const payload = {
                items: [
                    {
                        key: "markets-with-rising-trend",
                        variables: {
                            tenant: this.getTenant(),
                        },
                    },
                    {
                        key: "categories-with-declining-demand",
                        variables: {
                            tenant: this.getTenant(),
                        },
                    },
                ],
            };
            const response = await this.$store.dispatch(
                "partner/fetchBulkAnalyticReport",
                payload,
            );
            this.marketsWithRisingTrend =
                response?.data?.["markets-with-rising-trend"] ||
                response?.["markets-with-rising-trend"] ||
                [];
            this.categoriesWithDecliningDemand =
                response?.data?.["categories-with-declining-demand"] ||
                response?.["categories-with-declining-demand"] ||
                [];
        },
    },
    async mounted() {
        await this.fetchDashboardData();
        await this.fetchOrderVolumeForecast();
        await this.fetchFraudRiskForecast();
        await this.fetchLogisticsCapacity();
        await this.fetchItInfrastructureLoad();
        await this.fetchLogisticData();
        await this.fetchItData();
        await this.fetchPredictionInsights();
    },
};
</script>

<style>
.demand-prediction-page .dashboard-grid {
    margin-top: 1rem;
}

.dashboard-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 18px;
}
.charts-grid {
    margin-top: 22px;
    display: grid;
    gap: 20px;
    grid-template-columns: repeat(auto-fit, minmax(480px, 1fr));
}

.chart-card {
    background: white;
    border-radius: 12px;
    padding: 20px;
    border: 1px solid #e8e8e8;
    height: 100%;
}

.chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 14px;
}

.chart-header h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
}

.chart-toggles label {
    margin-left: 12px;
    font-size: 14px;
}

.risk-legend {
    display: flex;
    gap: 10px;
    align-items: center;
}

.legend-dot {
    height: 10px;
    width: 10px;
    border-radius: 50%;
    display: inline-block;
}

.legend-dot.low {
    background: #2ecc71;
}
.legend-dot.medium {
    background: #f1c40f;
}
.legend-dot.high {
    background: #e74c3c;
}

.risk-details {
    background: #fafafa;
    border-radius: 12px;
    padding: 14px;
    margin-top: 16px;
}

.risk-details ul {
    margin-top: 8px;
    padding-left: 20px;
}
.section-title {
    font-size: 22px;
    font-weight: 600;
    margin: 25px 0 15px;
}

.capacity-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(480px, 1fr));
    gap: 20px;
}

.capacity-card {
    background: white;
    border-radius: 12px;
    padding: 20px;
    border: 1px solid #e8e8e8;
}

.chart-title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 12px;
}

.capacity-info-row {
    display: flex;
    gap: 15px;
    margin-top: 18px;
    flex-wrap: wrap;
}

.capacity-info-box {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 18px;
    border-radius: 12px;
    min-width: 180px;
}

.capacity-info-box i {
    font-size: 18px;
}

.capacity-metric-box {
    align-items: flex-start;
}

.capacity-metric-box i {
    margin-top: 0.15rem;
}

.label {
    font-size: 14px;
    color: #666;
}

.value {
    font-size: 18px;
    font-weight: 600;
}

.info-blue {
    background: #eef6ff;
    color: #2980b9;
}

.info-green {
    background: #e9fdf0;
    color: #27ae60;
}

.info-orange {
    background: #fff2e6;
    color: #e67e22;
}

.info-purple {
    background: #f7e9ff;
    color: #8e44ad;
}

.info-green-light {
    background: #ecfdf5;
    color: #16a34a;
}
.insight-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 20px;
    margin-bottom: 40px;
}

.insight-card {
    background: white;
    border-radius: 14px;
    padding: 20px;
    border: 1px solid #e8e8e8;
    min-height: 180px;
}

.insight-icon {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 12px;
}

.insight-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 10px;
}

.insight-card ul {
    padding-left: 18px;
    margin: 0;
}

.insight-card li {
    color: #555;
    margin-bottom: 6px;
    font-size: 14px;
}

.icon-blue {
    background: #e8f3ff;
    color: #3a7afe;
}

.icon-green {
    background: #e6fcee;
    color: #2ecc71;
}

.icon-orange {
    background: #fff3e5;
    color: #e67e22;
}

.icon-purple {
    background: #f4eaff;
    color: #9b59b6;
}
.charts-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
}

@media (max-width: 768px) {
    .charts-grid {
        grid-template-columns: 1fr;
    }
}
.chart-card {
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
    box-sizing: border-box;
}
.capacity-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
}

@media (max-width: 768px) {
    .capacity-grid {
        grid-template-columns: 1fr;
    }
}
.capacity-card {
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
    box-sizing: border-box;
}
@media (max-width: 575.98px) {
  .capacity-info-row {
    display: flex;
    flex-direction: column;
    align-items: center;     
    justify-content: center; 
  }

  .capacity-info-box {
    width: 90%;              
    max-width: 320px;
    margin: 0.5rem auto;     
    justify-content: center;
    text-align: center;
  }

  .capacity-info-box i {
    margin-bottom: 0.25rem;
  }

  .capacity-metric-box {
    justify-content: center;
  }
}
</style>
